import { Briefcase, DollarSign, Bell, Check, X, Ban, Download, Edit2, Trash2, CheckCircle2, Lock, Wallet, Percent, ExternalLink, RefreshCw } from "lucide-react";
import type { AdminOrg } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { orgs as orgsApi } from "../../api/endpoints";
import { downloadCsv } from "../../exportCsv";
import { ConfirmState } from "../../components/feedback/ConfirmModal";
import { AddToast } from "../../components/feedback/Toast";
import { OrgAvatar } from "../../components/ui/Avatar";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { KpiCard } from "../../components/ui/KpiCard";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { openInApp, sendNotificationTo } from "../shared/detailActions";
import { NotesCard, focusNotes } from "../shared/NotesCard";
import { F, T } from "../../theme/tokens";
import { BackLink } from "../../components/ui/BackLink";
import { DetailActionBar } from "../../components/ui/DetailActionBar";

// ── Organisation detail ──────────────────────────────────────────────────────

export function OrgDetailView({ org, onBack, addToast, confirm }: { org: AdminOrg; onBack: () => void; addToast: AddToast; confirm: (opts: Omit<ConfirmState, "open">) => void }) {
  const { ADMIN_POSTINGS, ADMIN_PAYMENTS, refetchOrgs } = useAdminData();
  const relatedPostings = ADMIN_POSTINGS.filter((p) => p.orgId === org.id);
  const relatedPayments = ADMIN_PAYMENTS.filter((p) => p.orgId === org.id);

  // The app stores KYB as a plain status on `orgs` and treats anything other
  // than "Verified" as unverified, so rejecting is setting it back.
  function setKyb(status: "Verified" | "Rejected") {
    orgsApi.update(org.id, { kyb_status: status === "Verified" ? "Verified" : "Unverified" })
      .then(() => { refetchOrgs(); addToast(status === "Verified" ? `${org.name} KYB approved` : "KYB rejected", status === "Verified" ? "success" : "error"); })
      .catch(() => addToast("Failed to update KYB status", "error"));
  }
  function setAccountStatus(status: "Active" | "Suspended") {
    orgsApi.update(org.id, { account_status: status })
      .then(() => { refetchOrgs(); addToast(`${org.name} ${status === "Suspended" ? "suspended" : "reactivated"}`, status === "Suspended" ? "error" : "success"); })
      .catch(() => addToast("Failed to update account status", "error"));
  }
  const closed = org.account !== "Active";
  const archived = org.account === "Archived";
  function exportOrgData() {
    const count = downloadCsv(`donatella-${org.id}`, relatedPayments, [
      { header: "Transaction", value: (p) => p.txnRef },
      { header: "Engagement", value: (p) => p.postingTitle },
      { header: "Talent", value: (p) => p.talentName },
      { header: "Amount", value: (p) => p.amount },
      { header: "Platform fee", value: (p) => p.platformFee },
      { header: "Net to talent", value: (p) => p.netToTalent },
      { header: "Status", value: (p) => p.status },
      { header: "Date", value: (p) => p.date },
    ]);
    addToast(`Exported ${count} transactions`, "success");
  }

  function deleteOrg() {
    orgsApi.remove(org.id)
      .then(() => { refetchOrgs(); addToast(`${org.name} archived`, "error"); onBack(); })
      .catch(() => addToast("Failed to delete organisation", "error"));
  }

  return (
    <div>
      <BackLink onClick={onBack} label="Back to Organisations" />
      <div className="detail-head" style={{ display: "flex", alignItems: "flex-start", gap: "20px", marginBottom: "28px", flexWrap: "wrap" }}>
        <OrgAvatar className="detail-avatar" initials={org.initials} color={org.color} size={64} />
        <div className="detail-info" style={{ flex: 1 }}>
          <div className="detail-title" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <h1 style={{ fontSize: "20px", fontWeight: 700, color: T.text, fontFamily: F, letterSpacing: "-0.02em", margin: 0 }}>{org.name}</h1>
            <StatusBadge status={org.kyb} />
            <StatusBadge status={org.account} />
          </div>
          <p style={{ fontSize: "13px", color: T.sub, fontFamily: F, marginTop: "4px" }}>{org.industry} · {org.location}</p>
          <p style={{ fontSize: "12px", color: T.muted, fontFamily: F, marginTop: "2px" }}>CR: {org.crNumber} · {org.email}</p>
          <p style={{ fontSize: "12px", color: T.muted, fontFamily: F }}>{org.contact} ({org.contactRole})</p>
        </div>
        <div className="detail-actions" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Btn variant="ghost" onClick={() => openInApp(`/orgs/${org.id}`, addToast)}><ExternalLink size={13} /> View profile</Btn>
          {org.kyb === "Pending" && (
            <>
              <Btn variant="primary" onClick={() => setKyb("Verified")}><Check size={13} /> Approve KYB</Btn>
              <Btn variant="destructive" onClick={() => setKyb("Rejected")}><X size={13} /> Reject</Btn>
            </>
          )}
          {org.account === "Active" && (
            <Btn variant="ghost" onClick={() => confirm({ title: `Suspend ${org.name}?`, body: "This will deactivate all active postings.", destructive: true, confirmLabel: "Suspend", onConfirm: () => setAccountStatus("Suspended") })}>
              <Ban size={13} /> Suspend
            </Btn>
          )}
          {closed && (
            <Btn variant="ghost" onClick={() => setAccountStatus("Active")}><RefreshCw size={13} /> {archived ? "Restore account" : "Reactivate"}</Btn>
          )}
        </div>
      </div>

      <div className="rg-6" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: "12px", marginBottom: "22px" }}>
        <KpiCard icon={<Briefcase size={15} />} label="Active postings" value={org.activePostings.toString()} />
        <KpiCard icon={<CheckCircle2 size={15} />} label="Total hires" value={org.totalHires.toString()} />
        <KpiCard icon={<DollarSign size={15} />} label="Total spend" value={org.totalSpend} />
        <KpiCard icon={<Wallet size={15} />} label="Wallet balance" value={org.walletBalance} />
        <KpiCard icon={<Lock size={15} />} label="Escrow held" value={org.escrowHeld} />
        <KpiCard icon={<Percent size={15} />} label="Platform fee" value={org.customFee !== undefined ? `${org.customFee}% (custom)` : `${10}% (default)`} />
      </div>

      <div className="rg-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "14px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Card style={{ padding: "20px" }}>
            <SectionLabel>Postings</SectionLabel>
            {relatedPostings.length === 0 ? <p style={{ fontSize: "12px", color: T.muted, fontFamily: F }}>No postings.</p> : relatedPostings.map((p, i) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < relatedPostings.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <div>
                  <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{p.title}</p>
                  <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{p.category} · {p.budget}</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <p style={{ fontSize: "10px", color: T.muted, fontFamily: F }}>{p.totalApplications} applicants</p>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </Card>
          <Card style={{ padding: "20px" }}>
            <SectionLabel>Payment history</SectionLabel>
            {relatedPayments.length === 0 ? <p style={{ fontSize: "12px", color: T.muted, fontFamily: F }}>No payments.</p> : relatedPayments.map((p, i) => (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < relatedPayments.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <div>
                  <p style={{ fontSize: "11px", fontWeight: 600, color: T.text, fontFamily: F }}>{p.amount}</p>
                  <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{p.postingTitle} · {p.talentName}</p>
                  <p style={{ fontSize: "9px", color: T.muted, fontFamily: "'Space Mono', monospace" }}>{p.date} · {p.txnRef}</p>
                </div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </Card>
          <NotesCard entityType="Organisation" entityId={org.id} addToast={addToast} />
        </div>
        <Card className="hide-phone" style={{ padding: "20px" }}>
          <SectionLabel>Admin actions</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {!closed && <Btn variant="ghost" onClick={() => sendNotificationTo("org", org.id, org.name, addToast)} style={{ justifyContent: "flex-start", width: "100%" }}><Bell size={12} /> Send notification</Btn>}
            <Btn variant="ghost" onClick={focusNotes} style={{ justifyContent: "flex-start", width: "100%" }}><Edit2 size={12} /> Add internal note</Btn>
            <Btn variant="ghost" onClick={exportOrgData} style={{ justifyContent: "flex-start", width: "100%" }}><Download size={12} /> Export data</Btn>
            {!archived && (
              <Btn variant="destructive" onClick={() => confirm({ title: `Archive ${org.name}?`, body: "They can no longer sign in. Postings, engagements and payment history are kept — nothing on this platform is ever hard-deleted.", destructive: true, confirmLabel: "Archive organisation", onConfirm: deleteOrg })} style={{ justifyContent: "flex-start", width: "100%" }}>
                <Trash2 size={12} /> Archive organisation
              </Btn>
            )}
          </div>
        </Card>
      </div>

      <DetailActionBar
        onDelete={archived ? undefined : () => confirm({ title: `Archive ${org.name}?`, body: "They can no longer sign in. Postings, engagements and payment history are kept — nothing on this platform is ever hard-deleted.", destructive: true, confirmLabel: "Archive organisation", onConfirm: deleteOrg })}
        items={[
          ...(!closed ? [{ label: "Send notification", icon: <Bell size={15} />, onClick: () => sendNotificationTo("org", org.id, org.name, addToast) }] : []),
          { label: "Add internal note", icon: <Edit2 size={15} />, onClick: focusNotes },
          { label: "Export data", icon: <Download size={15} />, onClick: exportOrgData },
        ]}
      />
    </div>
  );
}
